import * as Sentry from "@sentry/react"

import { LOCAL_STORAGE_WELCOME_TOKEN_KEY } from "./localStorageKeys"
import { Flow, log } from "./logging"
import { WelcomeToken } from "./types/local"

/**
 * Session-based storage for welcome tokens
 * Uses in-memory cache + sessionStorage for persistence within a browser tab
 */
class WelcomeTokenStorage {
  private key: string
  private inMemoryData: WelcomeToken | null

  /**
   * @param key storage key for sessionStorage
   */
  constructor() {
    this.key = LOCAL_STORAGE_WELCOME_TOKEN_KEY
    this.inMemoryData = null
  }

  /**
   * Store the session data in memory and sessionStorage
   * @param data the session data containing qrLookupID and token
   */
  public setSessionData = (data: WelcomeToken): void => {
    this.inMemoryData = data

    try {
      sessionStorage.setItem(this.key, JSON.stringify(data))
    } catch (error) {
      // SessionStorage might be full or disabled
      Sentry.captureException(error)
      console.error("Failed to save to sessionStorage:", error)
    }
  }

  /**
   * Retrieve the session data, checking in-memory → sessionStorage
   * Caches the result in memory.
   */
  public getWelcomeToken = (): WelcomeToken | null => {
    // 1) in-memory
    if (this.inMemoryData) {
      log({
        level: "info",
        scope: "info.welcomeToken",
        flow: "misc",
        message: "Using in-memory session data",
        meta: { welcomeToken: this.inMemoryData },
        style: tokenFromLogStyle,
      })
      return this.inMemoryData
    }

    // 2) sessionStorage
    try {
      const stored = sessionStorage.getItem(this.key)
      if (stored) {
        const parsed = JSON.parse(stored) as WelcomeToken
        log({
          level: "info",
          scope: "info.welcomeToken",
          flow: "misc",
          message: "Using sessionStorage session data",
          meta: { welcomeToken: this.inMemoryData },
          style: tokenFromLogStyle,
        })
        this.inMemoryData = parsed
        return parsed
      }
    } catch (error) {
      Sentry.captureException(error)
      console.error("Failed to read from sessionStorage:", error)
    }
    console.log(
      "%cNo session data in memory or sessionStorage",
      tokenFromLogStyle
    )
    return null
  }

  /**
   * Get just the token from session data
   */
  public getToken = (): string | null => {
    const data = this.getWelcomeToken()
    return (data && data.token) || null
  }

  /**
   * Get just the qrLookupID from session data
   */
  public getQrLookupID = (): string | null => {
    const data = this.getWelcomeToken()
    return (data && data.qrLookupID) || null
  }

  /**
   * Get just the epochTime from session data
   * This represents the session start time and should be consistent across all routes
   */
  public getEpochTime = (): string | null => {
    const data = this.getWelcomeToken()
    return (data && data.epochTime) || null
  }

  /**
   * Validate that the provided qrLookupID matches the stored one
   * @param suppliedQrLookupID the qrLookupID from the URL
   * @returns true if it matches or no stored data exists, false if mismatch
   */
  public validateQrLookupID = (suppliedQrLookupID: string): boolean => {
    const storedQrLookupID = this.getQrLookupID()

    if (!storedQrLookupID) {
      log({
        level: "log",
        flow: "misc",
        scope: "validation.qrLookupID",
        message: "Missing QrLookupID",
        meta: {
          storedQrLookupID,
          suppliedQrLookupID,
        },
      })
      return false
    }

    // Check if URL qrLookupID matches stored qrLookupID
    const isValid = storedQrLookupID === suppliedQrLookupID

    if (!isValid) {
      log({
        level: "log",
        flow: "misc",
        scope: "validation.qrLookupID",
        message: "QrLookupID mismatch",
        meta: {
          storedQrLookupID,
          suppliedQrLookupID,
        },
      })
    }
    return isValid
  }

  /**
   * Validate that the epoch from the URL is not older than the stored token epoch
   * @param epoch the epoch time from the URL
   * @returns true if URL epoch is current/valid, false if it's from an old session
   */
  public validateEpochFromUrl = (args: {
    epoch: string
    flow: Flow
  }): boolean => {
    const storedEpoch = parseInt(this.getEpochTime() || "")
    const suppliedEpoch = parseInt(args.epoch)

    if (!storedEpoch) {
      log({
        level: "log",
        flow: args.flow,
        scope: "validation.epoch",
        message: "Missing epoch time",
        meta: {
          storedEpoch,
          suppliedEpoch,
        },
      })
      return false
    }

    if (suppliedEpoch !== storedEpoch) {
      log({
        level: "log",
        flow: args.flow,
        scope: "validation.epoch",
        message: "Epoch time mismatch",
        meta: {
          storedEpoch,
          suppliedEpoch,
        },
      })
      return false
    }

    return true
  }

  /** Remove the session data from memory and sessionStorage */
  public removeToken = (): void => {
    this.inMemoryData = null

    try {
      sessionStorage.removeItem(this.key)
    } catch (error) {
      Sentry.captureException(error)
      console.error("Failed to remove from sessionStorage:", error)
    }
  }
}

const tokenFromLogStyle = ["color: #0EA5E9", "font-weight: bold"].join(";")

const welcomeTokenStorage = new WelcomeTokenStorage()

export default welcomeTokenStorage
